/*
 * Qualität und Alter — die Übersetzung zwischen Server-Wortschatz und
 * Widget-Wortschatz.
 *
 * Warum das ein eigenes Modul mit Tests ist: hier wird still falsch.
 * Ein Renderer, der einen 40 Minuten alten Wert wie eine frische Messung
 * zeigt, wirft keinen Fehler, loggt nichts und sieht auf dem Screenshot
 * korrekt aus. In einer Heizungsanlage ist das kein Anzeigefehler, sondern
 * ein Sicherheitsproblem: der Techniker entscheidet über einen Sollwert
 * anhand einer Zahl, die die Anlage längst nicht mehr behauptet.
 *
 * Es gibt drei unabhängige Gründe, einem Wert nicht zu trauen, und alle
 * drei müssen durchkommen:
 *
 *   1. Der Server sagt es. `q` im SignalR-Batch, PascalCase, sechs Werte
 *      (`docs/hvacloud-client-spec.md` §5.2). `Bad` = letzter Read hat
 *      geworfen, `Uncertain` = Wert da aber Vertrauen begrenzt, `Stale` =
 *      älter als das Poll-Intervall, `Disconnected` = Transport zum Gerät
 *      weg, `Override` = der Wert kommt aus einem Force, nicht vom Sensor.
 *
 *   2. Der Transport zu UNS ist weg. Der Hub kann getrennt sein, während
 *      der letzte empfangene Wert `Good` war. Dann ist die Zahl auf dem
 *      Schirm die letzte bekannte, nicht die aktuelle
 *      (`realtime/hubClient.ts`, `ConnectionState`).
 *
 *   3. Der Wert ist einfach alt. Niemand behauptet etwas Falsches, es kam
 *      nur nichts Neues. Der Server setzt `Stale` nicht garantiert — er
 *      publisht mit Deadband, ein unveränderter Wert erzeugt gar keine
 *      Nachricht. Alter ist damit eine rein clientseitige Feststellung, und
 *      zwar über die lokale Empfangszeit, nicht über die Server-Uhr (siehe
 *      `PointValue.receivedAt`).
 *
 * Die Alters-Schwelle ist KEINE Konstante. Sie kommt pro Datenpunkt aus
 * `hmi/staleness.ts` (Poll-Intervall und Heartbeat aus dem Anlagen-Schema)
 * und wird hier als Parameter hereingegeben. Der Grund steht ausführlich im
 * Kopf jener Datei; die Kurzfassung: eine feste Schwelle dimmt einen
 * Zähler mit 15-Minuten-Poll dauerhaft, und ein HMI, das grundsätzlich grau
 * aussieht, wird ignoriert.
 *
 * Das Wort "live" (10-s-Fenster) ist dagegen weiter eine Konstante — es ist
 * eine Anzeige-Nuance und keine Sicherheitsaussage, und es ist aus
 * `realtime/freshness.ts` übernommen (Konzept B.5), damit das HMI und der
 * Datenpunkt-Baum nicht zwei Meinungen über dasselbe Wort haben.
 */

import type { ConnectionState } from '../realtime/hubClient';
import type { LeafQuality, LeafValue } from '../realtime/signalStore';
import type { PointQuality, PointValue } from './binding';

/** Wert gilt als frisch. Aus `realtime/freshness.ts` (Konzept B.5). Bleibt
 *  eine Konstante: reine Anzeige-Nuance, keine Sicherheitsaussage. */
export const LIVE_WINDOW_MS = 10_000;

/**
 * Server-Qualität (PascalCase) → normalisierte Stufe (lowercase).
 *
 * Eins zu eins, keine Abflachung. `ignoreCase` gibt es hier nicht, weil der
 * Server ausschließlich PascalCase sendet; ein unbekannter Wert wird
 * `'uncertain'` und wird geloggt — NICHT `'good'`. Fail-safe: lieber ein
 * gedimmter Wert als eine falsche Zusicherung von Frische.
 */
export function mapWireQuality(wire: string | null | undefined): PointQuality {
  switch (wire) {
    case 'Good':          return 'good';
    case 'Bad':           return 'bad';
    case 'Uncertain':     return 'uncertain';
    case 'Stale':         return 'stale';
    case 'Disconnected':  return 'disconnected';
    case 'Override':      return 'override';
    default:
      // Ein neuer Enum-Wert serverseitig, ein Tippfehler, oder ein
      // Snapshot-Endpoint mit anderer Konvention. Sichtbar machen: ein
      // stiller Fallback auf 'good' wäre genau der Fehler, den dieses
      // Modul verhindern soll.
      if (wire != null) {
        console.warn('[hmi/quality] unbekannte Server-Qualität, als uncertain behandelt', wire);
      }
      return 'uncertain';
  }
}

/** Gegenrichtung — für Tooltips, die die rohe Server-Aussage zeigen wollen,
 *  wenn `serverQuality` fehlt (Simulator, History-Aggregat). */
export function toWireQuality(quality: PointQuality): LeafQuality {
  switch (quality) {
    case 'good':          return 'Good';
    case 'bad':           return 'Bad';
    case 'uncertain':     return 'Uncertain';
    case 'stale':         return 'Stale';
    case 'disconnected':  return 'Disconnected';
    case 'override':      return 'Override';
  }
}

/**
 * Darf ein Renderer diesen Wert wie eine frische Messung behandeln?
 *
 * `false` heißt: ganz normal rendern. `true` heißt: dimmen, Overlay,
 * Alters-Hinweis — was das Widget eben tut.
 *
 * Bewusst als Prädikat und nicht als Vergleich gegen Literale: als
 * {@link PointQuality} um `disconnected` und `override` gewachsen ist, hätte
 * jedes `q === 'bad' || q === 'stale'` im Code die zwei neuen Werte
 * stillschweigend in den ok-Zweig fallen lassen. Ein Prädikat an einer
 * Stelle wächst mit.
 *
 * `override` zählt als degradiert: der Wert ist zwar aktuell, kommt aber aus
 * einem Force und nicht vom Sensor — ein Operator muss das sehen.
 */
export function isDegradedQuality(quality: PointQuality | null | undefined): boolean {
  return quality !== 'good';
}

/** Bequemlichkeits-Variante für den häufigsten Aufruf: `null` (noch kein
 *  Wert) gilt als degradiert. */
export function isDegraded(pv: { quality: PointQuality } | null | undefined): boolean {
  if (!pv) return true;
  return isDegradedQuality(pv.quality);
}

/**
 * Feinere Einordnung als {@link isDegraded} — für Widgets, die zwischen
 * "alt" und "ungültig" unterscheidend rendern wollen.
 *
 * Reihenfolge der Auswertung entspricht `realtime/freshness.ts`, damit
 * beide Oberflächen dasselbe Wort für denselben Zustand benutzen.
 */
export type PointFreshness = 'live' | 'aging' | 'stale' | 'bad' | 'disconnected' | 'override' | 'unknown';

export function classifyFreshness(
  pv: PointValue | null | undefined,
  staleAfterMs: number,
  now: number = Date.now(),
): PointFreshness {
  if (!pv) return 'unknown';
  if (pv.quality === 'override') return 'override';
  if (pv.quality === 'bad') return 'bad';
  if (pv.quality === 'disconnected') return 'disconnected';
  if (pv.quality === 'stale') return 'stale';
  const age = ageMs(pv, now);
  if (age > staleAfterMs) return 'stale';
  if (age > LIVE_WINDOW_MS) return 'aging';
  return pv.quality === 'uncertain' ? 'aging' : 'live';
}

/** Alter in Millisekunden, aus der lokalen Empfangszeit. Nie negativ —
 *  ein zurückgestellter `Date.now()` soll keinen Wert künstlich verjüngen. */
export function ageMs(pv: { receivedAt: number } | null | undefined, now: number = Date.now()): number {
  if (!pv) return Number.POSITIVE_INFINITY;
  return Math.max(0, now - pv.receivedAt);
}

/**
 * Deutsches Alters-Label für Tooltips und Alters-Badges: "gerade",
 * "vor 40 min", "vor 3 h".
 *
 * Bewusst grob — auf die Sekunde genau hilft niemandem, und eine
 * Sekundenanzeige müsste jede Sekunde neu rendern.
 */
export function formatAgeDe(pv: { receivedAt: number } | null | undefined, now: number = Date.now()): string {
  if (!pv) return 'kein Wert';
  const age = ageMs(pv, now);
  if (!Number.isFinite(age)) return 'kein Wert';
  if (age < LIVE_WINDOW_MS) return 'gerade';
  const seconds = Math.floor(age / 1000);
  if (seconds < 90) return `vor ${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 90) return `vor ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 36) return `vor ${hours} h`;
  return `vor ${Math.floor(hours / 24)} Tagen`;
}

/** Deutsches Label für eine Frische-Stufe. Wortwahl abgeglichen mit
 *  `realtime/freshness.ts` `freshnessLabelDe`. */
export function freshnessLabelDe(freshness: PointFreshness): string {
  switch (freshness) {
    case 'live':         return 'Live';
    case 'aging':        return 'Verzögert';
    case 'stale':        return 'Veraltet';
    case 'bad':          return 'Ungültig';
    case 'disconnected': return 'Getrennt';
    case 'override':     return 'Override';
    case 'unknown':      return 'Kein Wert';
  }
}

/**
 * Der eigentliche Adapter-Kern: ein `LeafValue` aus dem Signal-Store plus
 * der aktuelle Hub-Zustand plus die aktuelle Uhrzeit werden zu einem
 * `PointValue`, wie ihn die Widget-Schicht erwartet.
 *
 * Das ist die Stelle, an der Qualität und Alter durchkommen — oder eben
 * nicht. Reihenfolge der Anhebungen, von schwach nach stark:
 *
 *   1. Server-Qualität übernehmen (`mapWireQuality`). Nichts geht verloren:
 *      der rohe PascalCase-Wert reist als `serverQuality` mit.
 *   2. Ist der Hub nicht verbunden, wird `good` / `uncertain` / `stale` zu
 *      `disconnected`. `bad` und `override` bleiben — die sind spezifischer
 *      als "wir sehen gerade nichts" und würden durch das Überschreiben an
 *      Information verlieren.
 *   3. Ist der Wert älter als `staleAfterMs`, wird `good` / `uncertain` zu
 *      `stale`. Auch das ist eine Anhebung nach oben: ein `bad` bleibt
 *      `bad`, ein `override` bleibt `override`. Die Schwelle ist ein
 *      PARAMETER und keine Konstante — sie gehört dem Datenpunkt, nicht
 *      diesem Modul (`hmi/staleness.ts`, `staleAfterMsFor`).
 *
 * Eine Anhebung senkt nie: aus `bad` wird nie `good`, aus `disconnected`
 * nie `stale`. Wer das ändert, macht die Anzeige optimistischer als die
 * Datenlage — und genau darum geht es hier.
 */
export function projectLeafValue(
  leaf: LeafValue,
  connection: ConnectionState,
  staleAfterMs: number,
  now: number = Date.now(),
): PointValue {
  const serverQuality = leaf.q;
  let quality = mapWireQuality(serverQuality);

  if (connection !== 'connected' && (quality === 'good' || quality === 'uncertain' || quality === 'stale')) {
    quality = 'disconnected';
  }

  if ((quality === 'good' || quality === 'uncertain') && now - leaf.receivedAt > staleAfterMs) {
    quality = 'stale';
  }

  return {
    value: leaf.v,
    quality,
    timestamp: leaf.t,
    receivedAt: leaf.receivedAt,
    serverQuality,
  };
}

/**
 * Platzhalter-Wert für einen Punkt, für den strukturell nichts kommen kann
 * (nicht auflösbarer Binding-Key, Anlage nicht abonniert).
 *
 * Wichtig, dass es überhaupt einen Wert gibt: `null` heißt in der
 * Widget-Schicht "warte noch", und ein Renderer wartet dann für immer mit
 * einem Spinner, der aussieht wie "die Anlage antwortet nicht". Ein
 * `bad`-Wert sagt stattdessen sichtbar: hier kommt nichts.
 */
export function unresolvedPointValue(now: number = Date.now()): PointValue {
  return {
    value: null,
    quality: 'bad',
    timestamp: new Date(now).toISOString(),
    receivedAt: now,
  };
}
